import { type TemplateResult, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { TailwindElement } from '../../shared/tailwind.element';
import { questionaireContext } from '../../context/context';
import { consume } from '@lit/context';
import { type Message, type Answer, type AnswerData, type QuestionaireData } from '../../interfaces/interfaces';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { repeat } from 'lit/directives/repeat.js';
import { type Ref, createRef, ref } from 'lit/directives/ref.js';
import { addClarificationAttributes } from '../../shared/clarificationUtil';
import { CustomEventNames } from '../../constants/custom-event-names';
import { ClarificationClass } from '../../constants/constants';
import { ifDefined } from 'lit/directives/if-defined.js';
import { textItems } from '../../constants/text';

@customElement('szw-ba-question')
export class SzwBaQuestion extends TailwindElement() {
	@consume({ context: questionaireContext, subscribe: true })
	@property({ attribute: false })
	data!: QuestionaireData;

	@state()
	showError = false;

	@property({ attribute: false })
	blockingMessage?: Message = undefined;

	private readonly formRef: Ref<HTMLFormElement> = createRef();
	private readonly boundHandleKeyPress: (event: KeyboardEvent) => void;
	private readonly boundHandleSubmit: (event: Event) => void;
	private readonly boundResetError: (_: Event) => void;
	private clarificationClickHandler?: (event: Event) => void;

	constructor() {
		super();
		this.boundHandleKeyPress = this.handleKeyPress.bind(this);
		this.boundHandleSubmit = this.handleSubmit.bind(this);
		this.boundResetError = this.resetError.bind(this);
	}

	connectedCallback(): void {
		super.connectedCallback();
		void this.updateComplete.then(() => {
			const form = this.formRef.value;
			if (form != null) {
				form.addEventListener('keypress', this.boundHandleKeyPress);
				form.addEventListener('submit', this.boundHandleSubmit);
				form.addEventListener('change', this.boundResetError);
			}
		});
		this.addEventListener(CustomEventNames.SubmitForm, this.boundHandleSubmit);
		this.addEventListener(CustomEventNames.OpenQuestion, this.boundResetError);
	}

	updated(changedProperties: Map<string | number | symbol, unknown>): void {
		super.updated(changedProperties);
		if (changedProperties.has('data')) {
			if (this.data?.givenAnswer?.id != null)
				this.handleAnswerSelection(
					true,
					this.data?.question?.answers.find((answer) => answer.id === this.data?.givenAnswer?.id) as Answer,
				);
			this.formRef.value?.scrollIntoView({ behavior: 'auto' });
			this.clarificationClickHandler = addClarificationAttributes(this.shadowRoot, this);
			const mainContent = document.querySelector('main') as HTMLElement;
			if (mainContent != null) {
				mainContent.setAttribute('tabindex', '-1');
				mainContent.focus();
			}
		}
	}

	disconnectedCallback(): void {
		super.disconnectedCallback();
		const form = this.formRef.value;
		if (form != null) {
			form.removeEventListener('keypress', this.boundHandleKeyPress);
			form.removeEventListener('submit', this.boundHandleSubmit);
			form.removeEventListener('change', this.boundResetError);
		}
		this.removeEventListener(CustomEventNames.SubmitForm, this.boundHandleSubmit);
		this.removeEventListener(CustomEventNames.OpenQuestion, this.boundResetError);
		if (this.clarificationClickHandler != null) {
			this.shadowRoot?.querySelectorAll(ClarificationClass).forEach((item) => {
				if (this.clarificationClickHandler != null) {
					item.removeEventListener('click', this.clarificationClickHandler);
				}
			});
		}
	}

	resetError(_: Event): void {
		this.showError = false;
	}

	handleKeyPress(event: KeyboardEvent): void {
		if (event.code === 'Enter' && event.target instanceof HTMLInputElement) {
			event.preventDefault();
			this.handleSubmit(event);
		}
	}

	handleSubmit(event: Event): void {
		event.preventDefault();
		if (this.blockingMessage != null) return;
		if ((this.formRef.value?.checkValidity() ?? false) && this.data?.question != null) {
			this.showError = false;
			const formData = new FormData(this.formRef.value);
			const detail: AnswerData = {
				answerId: formData.get(this.data.question.id) as string,
				responseCode: this.data.responseCode,
				questionId: this.data.question.id,
			};
			const customEvent = new CustomEvent(CustomEventNames.AnswerQuestion, {
				detail,
				bubbles: true,
				composed: true,
			});
			this.dispatchEvent(customEvent);
		} else {
			this.showError = true;
			const eventTarget = event.target as HTMLElement;
			if (eventTarget !== null) eventTarget.scrollIntoView({ behavior: 'auto', block: 'center' });
			this.formRef.value?.reportValidity();
			this.dispatchEvent(new Event(CustomEventNames.LoadingDone, { bubbles: true, composed: true }));
		}
	}

	render(): TemplateResult {
		return html`
			<form class="mx-auto w-full max-w-3xl px-6" id="questionaireForm" ${ref(this.formRef)}>
				<fieldset>
					<legend class="mb-6 flex flex-col gap-6">
						<h1>${this.data?.question?.sectionTitle}</h1>
						<h2 class="text-base">${unsafeHTML(this.data?.question?.titleHtml)}</h2>
						${unsafeHTML(this.data?.question?.descriptionHtml)}
					</legend>
					${this.showError ? this.renderErrorMessageTemplate() : null}

					<div class="flex flex-col gap-4" role="radiogroup">
						${this.data?.question?.answers != null && this.data?.question?.answers.length > 0
							? repeat(
									this.data?.question?.answers,
									(answer) => answer.id,
									(answer) => this.renderAnswerTemplate(answer),
							  )
							: null}
					</div>
				</fieldset>
				<slot></slot>
			</form>
		`;
	}

	renderAnswerTemplate(answer: Answer): TemplateResult {
		return html`<label class="flex w-fit items-center gap-4" id="${answer.id}-label">
				<input
					?checked=${this.data?.givenAnswer?.id === answer.id}
					name="${ifDefined(this.data?.question?.id)}"
					type="radio"
					id="${answer.id}"
					value="${answer.id}"
					?required="${!(this.data?.question?.skipAnswers ?? false)}"
					aria-required="true"
					aria-labelledby="${answer.id}-label"
					aria-invalid=${this.showError}
					aria-errormessage="error-message"
					@change=${(event: Event) => {
						const eventTarget = event.target as HTMLInputElement;
						this.handleAnswerSelection(eventTarget.checked, answer);
					}}
				/>
				${unsafeHTML(answer.descriptionHtml)}</label
			>
			${answer.messages?.length > 0
				? html` ${answer.messages.map(
						(message) =>
							html`<div
								role="note"
								aria-hidden="true"
								class="message js-message !hidden"
								data-answer-id="${`message-${answer.id}`}"
							>
								<div>${unsafeHTML(message.descriptionHtml)}</div>
							</div>`,
				  )}`
				: null} `;
	}

	resetAnswer(): void {
		this.blockingMessage = undefined;
		const customEvent = new CustomEvent(CustomEventNames.UnblockQuestionaire, {
			bubbles: true,
			composed: true,
		});
		this.dispatchEvent(customEvent);
		// add !hidden class to all messages selected by class js-message
		this.shadowRoot?.querySelectorAll('.js-message').forEach((item) => {
			item.classList.add('!hidden');
			item.setAttribute('aria-hidden', 'true');
		});
	}

	handleChecked(answer: Answer): boolean {
		this.handleAnswerSelection(true, answer);
		return true;
	}

	handleAnswerSelection(isChecked: boolean, answer: Answer): void {
		if (!isChecked || answer == null) return;
		this.resetAnswer();
		if (this.data?.canOpenReport) {
			const customEvent = new CustomEvent(CustomEventNames.ShowResultBtn, {
				detail: this.data?.givenAnswer?.id === answer.id,
				bubbles: true,
				composed: true,
			});
			this.dispatchEvent(customEvent);
		}
		if (answer.messages?.length > 0) {
			const messages = this.shadowRoot?.querySelectorAll(`[data-answer-id="message-${answer.id}"]`);
			messages?.forEach((message) => {
				message.classList.remove('!hidden');
				message.setAttribute('aria-hidden', 'false');
			});
			// check if any of the messages is blocking
			this.blockingMessage = answer.messages.find((message) => message.isBlocking);
			if (this.blockingMessage != null) {
				const customEvent = new CustomEvent(CustomEventNames.BlockQuestionaire, {
					bubbles: true,
					composed: true,
				});
				this.dispatchEvent(customEvent);
			}
		}
	}

	renderErrorMessageTemplate(): TemplateResult {
		return html`<p class="mb-6 flex w-full gap-4 bg-rood-15 p-4" id="error-message" role="alert">
			<svg
				width="22"
				height="22"
				viewBox="0 0 22 22"
				class="shrink-0"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
				title="${textItems.icons.warning}"
			>
				<g>
					<g>
						<g>
							<path
								d="M11 21.5C16.799 21.5 21.5 16.799 21.5 11C21.5 5.20101 16.799 0.5 11 0.5C5.20101 0.5 0.5 5.20101 0.5 11C0.5 16.799 5.20101 21.5 11 21.5Z"
								fill="#D52B1E"
							/>
						</g>
						<path
							d="M14.1192 6.70993L10.9992 9.47993L7.8792 6.70993C7.5492 6.41993 7.0492 6.42993 6.7292 6.72993C6.4292 7.04993 6.4192 7.53993 6.7092 7.86993L9.4792 10.9999L6.7092 14.1199C6.4192 14.4499 6.4192 14.9499 6.7292 15.2599C7.0492 15.5599 7.5392 15.5699 7.8692 15.2799L10.9992 12.5199L14.1192 15.2899C14.4492 15.5799 14.9492 15.5699 15.2592 15.2699C15.5592 14.9499 15.5692 14.4599 15.2792 14.1299L12.5192 10.9999L15.2892 7.87993C15.5792 7.54993 15.5792 7.04993 15.2692 6.73993C14.9492 6.42993 14.4492 6.41993 14.1192 6.70993ZM10.9992 11.0099L10.9892 10.9999L10.9992 10.9899L11.0092 10.9799L11.0192 10.9899L11.0292 10.9999L10.9992 11.0099Z"
							fill="white"
						/>
					</g>
				</g>
			</svg>
			Er is nog geen antwoord gekozen. Kies één van de onderstaande antwoorden.
		</p>`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'szw-ba-question': SzwBaQuestion;
	}
}
